import type React from 'react';
import { type PropsWithChildren, useState } from 'react';
import Button from 'components/Globals/Base/Button';
import type { ButtonVariant } from 'components/Globals/Base/Button/types';
import * as S from './FiltersStyles';

export interface Props<T> {
  defaultData: T;
  onApplyFiltersClick: (value: T) => void;
  onResetFiltersClick: () => void;
  selectedFilters: T;
  setSelectedFilters: React.Dispatch<React.SetStateAction<T>>;
  className?: string;
  title?: string;
  disclaimer?: string;
  resultsCount?: number;
  initialExpandedState?: {
    mobile: boolean;
    tablet: boolean;
    desktop: boolean;
  };
  variant?: 'brand' | 'default';
}

const Filters = <T,>({
  children,
  defaultData,
  onApplyFiltersClick,
  onResetFiltersClick,
  selectedFilters,
  setSelectedFilters,
  className = '',
  title = 'Filters',
  disclaimer,
  resultsCount,
  initialExpandedState = {
    desktop: true,
    mobile: false,
    tablet: false,
  },
  variant = 'default',
}: PropsWithChildren<Props<T>>) => {
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(undefined);

  const handleHeaderClick = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleHeaderKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setIsExpanded((prev) => !prev);
    }
  };

  const handleApplyFiltersClick = () => {
    onApplyFiltersClick(selectedFilters);
  };

  const handleResetFiltersClick = () => {
    setSelectedFilters(defaultData);
    onResetFiltersClick();
  };

  const buttonsVariant: Record<string, ButtonVariant[]> = {
    brand: ['primaryBrand', 'tertiaryBrand'],
    default: ['primary', 'tertiary'],
  };

  const [applyFiltersVariant, resetFiltersVariant] = buttonsVariant[variant];

  return (
    <S.Wrapper
      data-cy='filters'
      className={className}
    >
      <S.Header
        onClick={handleHeaderClick}
        onKeyDown={handleHeaderKeyDown}
        tabIndex={0}
        aria-label='Filters Expand Button'
        data-cy='filters-expand-button'
      >
        <S.Title>{title}</S.Title>
      </S.Header>
      <S.Body
        $isExpanded={isExpanded}
        $initialExpandedState={initialExpandedState}
      >
        {children}
        <S.Footer>
          {resultsCount !== undefined && <S.ResultsCount>{resultsCount} results</S.ResultsCount>}
          <Button
            className='filters-button'
            variant={applyFiltersVariant}
            hasRightArrow={false}
            onClick={handleApplyFiltersClick}
            dataCy='filters-apply-button'
          >
            APPLY FILTERS
          </Button>
          <Button
            variant={resetFiltersVariant}
            hasRightArrow={false}
            onClick={handleResetFiltersClick}
            dataCy='filters-reset-button'
          >
            Reset Filters
          </Button>
        </S.Footer>
        {disclaimer && <S.DisclaimerText data-cy='filters-footer-disclaimer'>{disclaimer}</S.DisclaimerText>}
      </S.Body>
    </S.Wrapper>
  );
};

export default Filters;
